import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

export type TabBadge = {
  count: number;
  severity?: 'success' | 'warning' | 'danger';
};

export type WorkflowTab<T extends string = string> = {
  key: T;
  label: string;
  icon: string;
  badge?: TabBadge | null;
  locked?: boolean;
  loading?: boolean;
  disabled?: boolean;
  dropdownContent?: ReactNode;
};

export type WorkflowTabBarProps<T extends string = string> = {
  tabs: WorkflowTab<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  className?: string;
};

const BADGE_COLOR: Record<string, string> = {
  success: 'bg-green-500',
  warning: 'bg-yellow-500',
  danger: 'bg-red-500',
};

export const WorkflowTabBar = <T extends string = string>({
  tabs,
  activeTab,
  onTabChange,
  className,
}: WorkflowTabBarProps<T>) => {
  const [openDropdown, setOpenDropdown] = useState<T | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dropdownRefs = useRef<Map<T, HTMLDivElement>>(new Map());

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => clearCloseTimeout();
  }, [clearCloseTimeout]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (openDropdown === null) return;
      const ref = dropdownRefs.current.get(openDropdown);
      if (ref && !ref.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdown]);

  const handleMouseEnter = (tab: WorkflowTab<T>) => {
    if (!tab.dropdownContent) return;
    clearCloseTimeout();
    setOpenDropdown(tab.key);
  };

  const handleMouseLeave = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = setTimeout(() => setOpenDropdown(null), 200);
  };

  const setDropdownRef = (key: T, el: HTMLDivElement | null) => {
    if (el) {
      dropdownRefs.current.set(key, el);
    } else {
      dropdownRefs.current.delete(key);
    }
  };

  const renderTab = (tab: WorkflowTab<T>) => {
    const isDisabled = tab.locked || tab.loading || tab.disabled;
    const iconClass = tab.loading
      ? 'pi pi-spin pi-spinner'
      : tab.locked
        ? 'pi pi-lock'
        : tab.icon;

    const badgeColorClass = tab.badge
      ? BADGE_COLOR[tab.badge.severity ?? 'success']
      : '';

    const tabButton = (
      <button
        key={tab.key}
        type="button"
        onClick={() => !isDisabled && onTabChange(tab.key)}
        disabled={isDisabled}
        className={`flex flex-1 items-center justify-center gap-2 border-0 bg-transparent px-4 py-2 text-sm font-medium transition-colors md:flex-none ${
          activeTab === tab.key
            ? 'border-b-[3px] border-b-cyan-600 text-cyan-700'
            : 'border-b-[3px] border-b-transparent text-gray-500 hover:text-gray-700'
        } ${isDisabled ? 'cursor-not-allowed opacity-40' : ''}`}
      >
        <i className={`${iconClass} text-xs`} />
        <span>{tab.label}</span>
        {tab.badge && tab.badge.count > 0 && (
          <span
            className={`inline-block min-w-[18px] rounded-full px-1 text-center text-[10px] font-bold text-white ${badgeColorClass}`}
            style={{ paddingTop: '3px', paddingBottom: '2px', lineHeight: 1 }}
          >
            {tab.badge.count}
          </span>
        )}
      </button>
    );

    if (tab.dropdownContent) {
      return (
        <div
          key={tab.key}
          ref={(el) => setDropdownRef(tab.key, el)}
          className="relative"
          onMouseEnter={() => handleMouseEnter(tab)}
          onMouseLeave={handleMouseLeave}
        >
          {tabButton}
          {openDropdown === tab.key && tab.dropdownContent}
        </div>
      );
    }

    return tabButton;
  };

  return (
    <div
      data-tour="session-tabs"
      className={`flex border-b border-gray-200${className ? ` ${className}` : ''}`}
    >
      {tabs.map((tab) => renderTab(tab))}
    </div>
  );
};
